import { useState } from 'react';
import { Copy, Download } from 'lucide-react';
import api from '../lib/api';
import Button from '../ui/Button';
import { useToast } from '../ui/Toast';

// Transcript actions on src/ui (HD-6 Raw footage): Copy, TXT, SRT, VTT. Same calls as before:
// GET /api/transcriptions/:id/txt|srt|vtt as a blob through the authed client (the API is another origin behind Bearer
// auth, so a bare link would never reach it). Used by Raw footage and, since HD-8, the Review page's Details tab.

function saveBlob(data, filename, mime) {
  const url = URL.createObjectURL(new Blob([data], { type: mime }));
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

const FORMATS = [
  { key: 'txt', label: 'TXT', mime: 'text/plain;charset=utf-8' },
  { key: 'srt', label: 'SRT', mime: 'application/x-subrip;charset=utf-8' },
  { key: 'vtt', label: 'VTT', mime: 'text/vtt;charset=utf-8' },
];

export default function TranscriptDownloads({ transcriptionId, transcriptText, baseName }) {
  const toast = useToast();
  const [busy, setBusy] = useState(null);

  // "C2049.MP4" → "C2049.srt", so the transcript files sit next to the clip.
  const stem = (baseName || '').replace(/\.[^.]+$/, '') || `transcript_${String(transcriptionId).slice(0, 8)}`;

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(transcriptText || '');
      toast('Transcript copied.');
    } catch {
      toast('The transcript was not copied. Select the text and copy it by hand.', { tone: 'danger' });
    }
  }

  async function handleDownload(fmt) {
    setBusy(fmt.key);
    try {
      const res = await api.get(`/api/transcriptions/${transcriptionId}/${fmt.key}`, { responseType: 'blob' });
      saveBlob(res.data, `${stem}.${fmt.key}`, fmt.mime);
    } catch (err) {
      toast(err?.response?.status === 404 ? 'The transcript is not ready yet. Try again in a minute.' : `The ${fmt.label} download did not start. Try again.`, { tone: 'danger' });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="ui-cluster" role="group" aria-label="Transcript downloads">
      <Button variant="quiet" icon={Copy} onClick={handleCopy}>Copy</Button>
      {FORMATS.map(f => (
        <Button key={f.key} variant="quiet" icon={Download} loading={busy === f.key} disabled={!transcriptionId}
          title={`Download the transcript as ${f.label}`} onClick={() => handleDownload(f)}>
          {f.label}
        </Button>
      ))}
    </div>
  );
}
